import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInviteAuth } from '../pages/Invite/InviteAuthContext';
import API_BASE_URL, { getImageUrl } from '../config';
import {
    X,
    User as UserIcon,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Heart,
    Shield,
    Sparkles,
    Building,
    Camera,
    Copy,
    Check,
    ExternalLink,
    LogOut,
    Bed,
    Utensils,
    Ticket,
    Crown,
    MessageSquare,
    Send
} from 'lucide-react';
import './UserProfileModal.css';

const UserProfileModal: React.FC = () => {
    const { user, accessToken, isProfileModalOpen, closeProfileModal, logout } = useAuth();
    const { inviteUser, inviteToken, inviteLogout } = useInviteAuth();
    const history = useHistory();
    const location = useLocation();

    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [profileDetails, setProfileDetails] = useState<any>(null);
    const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

    const isInviteRoute = location.pathname.startsWith('/invite');
    const isInviteSpecific = Boolean((isInviteRoute && inviteUser) || (!user && inviteUser));
    const activeTarget = isInviteSpecific ? inviteUser : (user || inviteUser);

    // Fetch fresh profile data when modal opens
    useEffect(() => {
        if (!isProfileModalOpen || !activeTarget) return;

        let isMounted = true;
        const fetchDetails = async () => {
            setLoadingDetails(true);
            try {
                if (isInviteSpecific && inviteToken) {
                    const res = await fetch(`${API_BASE_URL}/api/invite/auth/profile`, {
                        headers: { Authorization: `Bearer ${inviteToken}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (isMounted && data.user) {
                            setProfileDetails(data.user);
                        }
                    }
                } else if (user && accessToken) {
                    let endpoint = '';
                    if (user.role === 'admin') endpoint = `${API_BASE_URL}/api/admin/profile`;
                    else if (user.role === 'guest') endpoint = `${API_BASE_URL}/api/guests/profile`;
                    else if (user.role === 'agency') endpoint = `${API_BASE_URL}/api/agency/profile`;
                    else if (user.role === 'photographer') endpoint = `${API_BASE_URL}/api/photographer/profile`;

                    if (endpoint) {
                        const res = await fetch(endpoint, {
                            headers: { Authorization: `Bearer ${accessToken}` }
                        });
                        if (res.ok) {
                            const data = await res.json();
                            if (isMounted) {
                                if (user.role === 'admin' && data.admin) {
                                    setProfileDetails(data.admin);
                                } else if (user.role === 'guest' && data.guest) {
                                    setProfileDetails(data.guest);
                                } else if (user.role === 'agency' && data.agency) {
                                    setProfileDetails(data.agency);
                                } else if (user.role === 'photographer' && data.photographer) {
                                    setProfileDetails(data.photographer);
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn('Could not fetch live profile details:', err);
            } finally {
                if (isMounted) setLoadingDetails(false);
            }
        };

        fetchDetails();

        return () => {
            isMounted = false;
        };
    }, [isProfileModalOpen, activeTarget, isInviteSpecific, inviteToken, user, accessToken]);

    // Close on Escape key press
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isProfileModalOpen) {
                closeProfileModal();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isProfileModalOpen, closeProfileModal]);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isProfileModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isProfileModalOpen]);

    if (!isProfileModalOpen || !activeTarget) return null;

    // Merge cached active target with live details
    const activeData: any = { ...activeTarget, ...(profileDetails || {}) };

    const getInitials = (name: string) => {
        if (!name) return 'U';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return parts[0].substring(0, 2).toUpperCase();
    };

    const handleCopy = (text: string, fieldId: string) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedField(fieldId);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleNavigate = (path: string) => {
        closeProfileModal();
        history.push(path);
    };

    const handleLogoutClick = () => {
        closeProfileModal();
        if (isInviteSpecific) {
            inviteLogout();
            localStorage.removeItem('shadigram_invite_wizard_draft');
            if (user) logout();
        } else {
            logout();
        }
        history.push('/');
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return null;
        try {
            const d = new Date(dateString);
            return d.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const getRoleBadge = () => {
        if (isInviteSpecific) {
            return {
                label: activeData.isPremium ? '★ Pro Studio Creator' : '✉️ Invite Studio Creator',
                icon: activeData.isPremium ? Crown : Sparkles,
                color: 'purple'
            };
        }
        switch (user?.role) {
            case 'admin':
                return { label: 'Wedding Admin', icon: Sparkles, color: 'gold' };
            case 'guest':
                return { label: 'Wedding Guest', icon: Heart, color: 'rose' };
            case 'superadmin':
                return { label: 'Super Admin', icon: Crown, color: 'purple' };
            case 'photographer':
                return { label: 'Photographer', icon: Camera, color: 'blue' };
            case 'agency':
                return { label: 'Agency Partner', icon: Building, color: 'amber' };
            default:
                return { label: user?.role || 'Member', icon: Shield, color: 'gray' };
        }
    };

    const roleBadge = getRoleBadge();
    const RoleIcon = roleBadge.icon;

    return (
        <div className="user-profile-modal-overlay" onClick={closeProfileModal}>
            <div
                className="user-profile-modal-container"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-profile-modal-title"
            >
                {/* Modal Header */}
                <div className="user-profile-modal-header">
                    <div className="modal-title-wrap">
                        <UserIcon size={18} className="modal-header-icon" />
                        <h2 id="user-profile-modal-title">
                            {isInviteSpecific ? 'Invitation Studio Profile' : 'User Profile'}
                        </h2>
                    </div>
                    <button
                        className="user-profile-modal-close-btn"
                        onClick={closeProfileModal}
                        aria-label="Close modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="user-profile-modal-body">
                    {/* Hero Card */}
                    <div className="user-profile-hero">
                        <div className="user-profile-avatar-wrap">
                            {activeData.profilePhoto ? (
                                <img
                                    src={getImageUrl(activeData.profilePhoto)}
                                    alt={activeData.name}
                                    className="user-profile-avatar-img"
                                />
                            ) : (
                                <div className="user-profile-avatar-initials">
                                    {getInitials(activeData.name || 'User')}
                                </div>
                            )}
                            <div className={`user-profile-role-dot role-${roleBadge.color}`} title={roleBadge.label}>
                                <RoleIcon size={12} />
                            </div>
                        </div>

                        <div className="user-profile-hero-info">
                            <h3 className="user-profile-hero-name">{activeData.name || 'User'}</h3>
                            <div className="user-profile-hero-badges">
                                <span className={`user-profile-badge badge-${roleBadge.color}`}>
                                    <RoleIcon size={12} />
                                    {roleBadge.label}
                                </span>

                                {isInviteSpecific ? (
                                    <span className="user-profile-badge badge-plan">
                                        {activeData.isPremium ? `★ ${(activeData.premiumPlan || 'Pro').toUpperCase()} PLAN` : 'FREE TIER'}
                                    </span>
                                ) : (
                                    <>
                                        {activeData.status && (
                                            <span className={`user-profile-badge badge-status status-${activeData.status}`}>
                                                ● {activeData.status.charAt(0).toUpperCase() + activeData.status.slice(1)}
                                            </span>
                                        )}
                                        {activeData.weddingPlan && (
                                            <span className="user-profile-badge badge-plan">
                                                ✨ {activeData.weddingPlan.toUpperCase()} PLAN
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Information Sections */}
                    <div className="user-profile-sections">
                        {/* Section: Contact & Basic Info */}
                        <div className="user-profile-section-card">
                            <h4 className="user-profile-section-title">Contact Information</h4>
                            <div className="user-profile-grid">
                                {activeData.email && (
                                    <div className="user-profile-item">
                                        <div className="user-profile-item-icon">
                                            <Mail size={16} />
                                        </div>
                                        <div className="user-profile-item-content">
                                            <span className="user-profile-label">Email Address</span>
                                            <span className="user-profile-value">{activeData.email}</span>
                                        </div>
                                        <button
                                            className="user-profile-copy-btn"
                                            onClick={() => handleCopy(activeData.email, 'email')}
                                            title="Copy email"
                                        >
                                            {copiedField === 'email' ? <Check size={14} className="copied" /> : <Copy size={14} />}
                                        </button>
                                    </div>
                                )}

                                {activeData.phone && (
                                    <div className="user-profile-item">
                                        <div className="user-profile-item-icon">
                                            <Phone size={16} />
                                        </div>
                                        <div className="user-profile-item-content">
                                            <span className="user-profile-label">Phone Number</span>
                                            <span className="user-profile-value">{activeData.phone}</span>
                                        </div>
                                        <button
                                            className="user-profile-copy-btn"
                                            onClick={() => handleCopy(activeData.phone, 'phone')}
                                            title="Copy phone"
                                        >
                                            {copiedField === 'phone' ? <Check size={14} className="copied" /> : <Copy size={14} />}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Section: Invitation Studio Quota & Stats (for Invite Studio context) */}
                        {isInviteSpecific && (
                            <div className="user-profile-section-card">
                                <h4 className="user-profile-section-title">Invitation Studio Quota & Stats</h4>
                                <div className="user-profile-grid">
                                    <div className="user-profile-item">
                                        <div className="user-profile-item-icon gold-icon">
                                            <Crown size={16} />
                                        </div>
                                        <div className="user-profile-item-content">
                                            <span className="user-profile-label">Subscription Tier</span>
                                            <span className="user-profile-value highlight-value">
                                                {activeData.isPremium ? `★ Pro Tier (${activeData.premiumPlan || 'Pro'})` : 'Free Account (10 Guest Limit)'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="user-profile-item">
                                        <div className="user-profile-item-icon">
                                            <Send size={16} />
                                        </div>
                                        <div className="user-profile-item-content">
                                            <span className="user-profile-label">Total Sent</span>
                                            <span className="user-profile-value">
                                                {activeData.totalInvitesSent || 0} Invitations Dispatched
                                            </span>
                                        </div>
                                    </div>

                                    <div className="user-profile-item">
                                        <div className="user-profile-item-icon">
                                            <Ticket size={16} />
                                        </div>
                                        <div className="user-profile-item-content">
                                            <span className="user-profile-label">Remaining Guest Quota</span>
                                            <span className="user-profile-value token-code">
                                                {activeData.remainingInvites ?? Math.max(0, (activeData.maxInvitations || 10) - (activeData.totalInvitesSent || 0))} of {activeData.maxInvitations || 10} Left
                                            </span>
                                        </div>
                                    </div>

                                    <div className="user-profile-item">
                                        <div className="user-profile-item-icon">
                                            <MessageSquare size={16} />
                                        </div>
                                        <div className="user-profile-item-content">
                                            <span className="user-profile-label">WhatsApp Status</span>
                                            <span className="user-profile-value">
                                                {activeData.whatsappStatus === 'connected' ? 'Connected 🟢' : 'Disconnected ⚪'}
                                                {activeData.whatsappPhoneNumber ? ` (${activeData.whatsappPhoneNumber})` : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section: Wedding Specific Details (Admin) */}
                        {!isInviteSpecific && user?.role === 'admin' && (
                            <div className="user-profile-section-card">
                                <h4 className="user-profile-section-title">Wedding Event Details</h4>
                                <div className="user-profile-grid">
                                    {(activeData.weddingCoupleName || activeData.wedding?.coupleName) && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon gold-icon">
                                                <Heart size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Couple Name</span>
                                                <span className="user-profile-value highlight-value">
                                                    {activeData.weddingCoupleName || activeData.wedding?.coupleName}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {(activeData.weddingDate || activeData.wedding?.weddingDate) && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon">
                                                <Calendar size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Wedding Date</span>
                                                <span className="user-profile-value">
                                                    {formatDate(activeData.weddingDate || activeData.wedding?.weddingDate)}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {(activeData.weddingVenue || activeData.wedding?.venue) && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon">
                                                <MapPin size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Venue</span>
                                                <span className="user-profile-value">
                                                    {activeData.weddingVenue || activeData.wedding?.venue}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {activeData.familySide && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon">
                                                <UserIcon size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Family Side</span>
                                                <span className="user-profile-value">
                                                    {activeData.familySide.charAt(0).toUpperCase() + activeData.familySide.slice(1)}'s Side
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Section: Guest Stay & Event Info */}
                        {!isInviteSpecific && user?.role === 'guest' && (
                            <div className="user-profile-section-card">
                                <h4 className="user-profile-section-title">Guest Details & Stay</h4>
                                <div className="user-profile-grid">
                                    {activeData.familySide && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon">
                                                <Heart size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Invited For</span>
                                                <span className="user-profile-value">
                                                    {activeData.familySide.charAt(0).toUpperCase() + activeData.familySide.slice(1)}'s Side
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {(activeData.roomNumber || activeData.hotelName) && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon">
                                                <Bed size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Accommodation</span>
                                                <span className="user-profile-value">
                                                    {activeData.hotelName ? `${activeData.hotelName} ` : ''}
                                                    {activeData.roomNumber ? `Room ${activeData.roomNumber}` : 'Stay Assigned'}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {activeData.dietaryInfo && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon">
                                                <Utensils size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Dietary Preference</span>
                                                <span className="user-profile-value">{activeData.dietaryInfo}</span>
                                            </div>
                                        </div>
                                    )}

                                    {(activeData.tokenNumber || activeData.giftCode) && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon gold-icon">
                                                <Ticket size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">Event / Gift Pass</span>
                                                <span className="user-profile-value token-code">
                                                    {activeData.tokenNumber || activeData.giftCode}
                                                </span>
                                            </div>
                                            <button
                                                className="user-profile-copy-btn"
                                                onClick={() => handleCopy(activeData.tokenNumber || activeData.giftCode, 'pass')}
                                                title="Copy pass token"
                                            >
                                                {copiedField === 'pass' ? <Check size={14} className="copied" /> : <Copy size={14} />}
                                            </button>
                                        </div>
                                    )}

                                    {activeData.location && (
                                        <div className="user-profile-item">
                                            <div className="user-profile-item-icon">
                                                <MapPin size={16} />
                                            </div>
                                            <div className="user-profile-item-content">
                                                <span className="user-profile-label">City / Location</span>
                                                <span className="user-profile-value">{activeData.location}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="user-profile-modal-footer">
                    {isInviteSpecific ? (
                        <>
                            <button
                                className="user-profile-btn btn-secondary"
                                onClick={() => handleNavigate('/invite/upgrade')}
                            >
                                <Crown size={15} />
                                Upgrade Package
                            </button>
                            <button
                                className="user-profile-btn btn-secondary"
                                onClick={() => handleNavigate('/invite/whatsapp/settings')}
                            >
                                <MessageSquare size={15} />
                                WhatsApp Setup
                            </button>
                        </>
                    ) : (
                        <>
                            {user?.role === 'admin' && (
                                <button
                                    className="user-profile-btn btn-secondary"
                                    onClick={() => handleNavigate('/admin/settings')}
                                >
                                    <ExternalLink size={15} />
                                    Wedding Settings
                                </button>
                            )}

                            {user?.role === 'guest' && (
                                <button
                                    className="user-profile-btn btn-secondary"
                                    onClick={() => handleNavigate('/dashboard')}
                                >
                                    <Sparkles size={15} />
                                    Guest Dashboard
                                </button>
                            )}
                        </>
                    )}

                    <button
                        className="user-profile-btn btn-logout"
                        onClick={handleLogoutClick}
                    >
                        <LogOut size={15} />
                        Logout
                    </button>

                    <button
                        className="user-profile-btn btn-primary"
                        onClick={closeProfileModal}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserProfileModal;
